/**
 * The loop every plan-system illustration runs: measure the box, bind the tracks, play or park —
 * and do it again whenever the box changes width.
 *
 * ── Why it is one hook and not three copies ──────────────────────────────────────────────────
 * The calendar illustration, the sequence illustration and the assign dialog's hop frame each wrote
 * out the same twenty lines: a `start` that re-measures and restarts, an explicit first call, a
 * `ResizeObserver` bound to the box, and a cleanup that disconnects and stops. Only the MEASURING
 * and the tracks it feeds differ, and that is the one thing a caller passes.
 *
 * ── The measurement cannot be a constant, on any of the three ────────────────────────────────
 * The mocks express these distances in `cqw`. The real boxes are grid columns whose width follows
 * the dialog, so a pitch derived from a token is right at exactly one viewport. Measuring says what
 * the drawing actually shows, and re-measuring on resize keeps it saying it.
 *
 * ── `restartKey` ─────────────────────────────────────────────────────────────────────────────
 * The two illustrations draw a fixed set of tiles, so nothing but the box's width and the two flags
 * can change their tracks. The hop frame's stops are the PLAN's programmed weekdays, which change
 * under it without the box resizing — so it names them here and the loop is rebuilt when they move.
 *
 * `reducedMotion` arrives as a parameter and never as a media query: the CSS block cannot reach a
 * Motion animation, which is the policy `shared/AGENTS.md` states.
 */

import { useLayoutEffect, useRef, type RefObject } from 'react';

import { runPlanSystemLoop, type LoopBinding } from './planSystemLoop';

export function useMeasuredLoop(
  /** The box that is measured and observed. */
  boxRef: RefObject<HTMLElement | null>,
  /** What to play, measured off the box. Called on mount, on every resize and on a restart. */
  buildBindings: (box: HTMLElement) => readonly LoopBinding[],
  options: {
    readonly reducedMotion: boolean;
    readonly running: boolean;
    /** Anything else that changes what is measured. See the header. */
    readonly restartKey?: string;
  },
): void {
  const { reducedMotion, running, restartKey = '' } = options;

  /**
   * The latest builder, so the effect below can be keyed on what actually restarts the loop rather
   * than on a closure identity that changes every render — a `ResizeObserver` torn down and rebound
   * on every keystroke in the dialog's search box is an observer that misses the resize it exists
   * for. Written in a layout effect declared FIRST, so it is already current when the loop's own
   * effect runs in the same commit.
   */
  const latest = useRef(buildBindings);
  useLayoutEffect(() => {
    latest.current = buildBindings;
  });

  useLayoutEffect(() => {
    const box = boxRef.current;
    if (box === null) return undefined;

    let stop: (() => void) | null = null;

    const start = () => {
      stop?.();
      stop = runPlanSystemLoop(latest.current(box), reducedMotion || !running);
    };

    start();
    const observer = new ResizeObserver(start);
    observer.observe(box);

    return () => {
      observer.disconnect();
      stop?.();
    };
  }, [boxRef, reducedMotion, running, restartKey]);
}
